import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import GlobalContext from '@/context/GlobalContext';
import { supabase } from '@/lib/supabase';
import type { CourseWithFullDetails } from '@/lib/types';
import CourseEditContext from '@/Pages/Courses/CourseEditContext';

export type AiFeature = 'generate_outcomes' | 'generate_units' | 'suggest_resources';

export interface ResourceSuggestion {
	type: 'Media' | 'Textbook';
	title: string;
	author?: string;
	isbn?: string;
	source?: string;
}

export type AiSuggestion = string | ResourceSuggestion;

interface CachedSlice {
	suggestions: AiSuggestion[];
	logId: string | null;
}

// Module-level cache — not reactive, so it adds zero subscriptions/re-renders.
// Survives tab switches but is cleared when the user leaves the course page.
const cache = new Map<string, CachedSlice>();

// Tracks how many active hook instances exist per course. When the count drops
// to zero (all tabs unmounted = user left the course), a deferred check clears
// that course's cache. Tab switches are safe because the new tab mounts before
// the deferred check fires.
const activeInstances = new Map<string, number>();

function bumpActive(courseId: string | undefined) {
	const k = courseId ?? '';
	activeInstances.set(k, (activeInstances.get(k) ?? 0) + 1);
}

function dropActive(courseId: string | undefined) {
	const k = courseId ?? '';
	const count = (activeInstances.get(k) ?? 1) - 1;
	activeInstances.set(k, count);
	if (count <= 0) {
		setTimeout(() => {
			if ((activeInstances.get(k) ?? 0) <= 0) {
				for (const key of cache.keys()) {
					if (key.startsWith(`${k}::`)) cache.delete(key);
				}
				activeInstances.delete(k);
			}
		}, 0);
	}
}

function cacheKey(courseId: string | undefined, feature: AiFeature) {
	return `${courseId ?? ''}::${feature}`;
}

interface UseAiSuggestReturn {
	suggest: () => Promise<void>;
	loading: boolean;
	suggestions: AiSuggestion[];
	panelOpen: boolean;
	setPanelOpen: (open: boolean) => void;
	logId: string | null;
	markAccepted: () => void;
	error: string | null;
}

const useAiSuggest = (feature: AiFeature): UseAiSuggestReturn => {
	const { currentUser } = useContext(GlobalContext);
	const { courseData, courseDraftData } = useContext(CourseEditContext) as unknown as {
		courseData: CourseWithFullDetails;
		courseDraftData: CourseWithFullDetails;
	};

	const courseId = courseData?.course_id;
	const key = cacheKey(courseId, feature);
	const cached = cache.get(key);

	// Local state — initialized from cache so suggestions survive tab switches.
	const [suggestions, setSuggestions] = useState<AiSuggestion[]>(cached?.suggestions ?? []);
	const [panelOpen, setPanelOpenRaw] = useState(!!cached?.suggestions?.length);
	const [logId, setLogId] = useState<string | null>(cached?.logId ?? null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Register/unregister this instance for cache-lifetime tracking.
	useEffect(() => {
		bumpActive(courseId);
		return () => dropActive(courseId);
	}, [courseId]);

	// When the key changes (different course), reset local state from cache.
	const prevKeyRef = useRef(key);
	useEffect(() => {
		if (prevKeyRef.current !== key) {
			prevKeyRef.current = key;
			const entry = cache.get(key);
			setSuggestions(entry?.suggestions ?? []);
			setPanelOpenRaw(!!entry?.suggestions?.length);
			setLogId(entry?.logId ?? null);
			setError(null);
		}
	}, [key]);

	// Keep cache in sync whenever suggestions/logId change.
	useEffect(() => {
		if (suggestions.length > 0) {
			cache.set(key, { suggestions, logId });
		} else {
			cache.delete(key);
		}
	}, [key, suggestions, logId]);

	const suggest = useCallback(async () => {
		if (!currentUser?.user_id || !currentUser?.school_id) return;

		setLoading(true);
		setError(null);
		setPanelOpenRaw(false);

		try {
			const existingOutcomes =
				feature === 'generate_outcomes'
					? ((courseDraftData as any).courses_outcomes
							?.map((o: any) => o.outcome?.outcome_text ?? '')
							.filter(Boolean) ?? [])
					: undefined;

			const { data, error: fnError } = await supabase.functions.invoke('ai-suggest', {
				body: {
					feature,
					courseId: courseData.course_id,
					schoolId: currentUser.school_id,
					userId: currentUser.user_id,
					courseName: courseData.course_name ?? '',
					courseDescription:
						courseDraftData.course_description ?? courseData.course_description ?? '',
					disciplineName: (courseData as any).courses_discipline?.discipline?.name ?? undefined,
					existingOutcomes,
				},
			});

			if (fnError) throw fnError;

			const newSuggestions: AiSuggestion[] = data?.suggestions ?? [];
			const newLogId: string | null = data?.logId ?? null;

			setSuggestions(newSuggestions);
			setLogId(newLogId);
			setPanelOpenRaw(true);
		} catch (err) {
			console.error('useAiSuggest error:', err);
			setError('Could not generate suggestions. Please try again.');
		} finally {
			setLoading(false);
		}
	}, [feature, courseData, courseDraftData, currentUser]);

	const setPanelOpen = useCallback(
		(open: boolean) => {
			if (!open) {
				setSuggestions([]);
				setLogId(null);
				cache.delete(key);
			}
			setPanelOpenRaw(open);
		},
		[key]
	);

	const markAccepted = useCallback(() => {
		if (!logId) return;
		supabase.functions.invoke('ai-mark-accepted', { body: { logId } }).then(({ error: err }) => {
			if (err) console.error('Failed to mark AI log as accepted:', err);
		});
	}, [logId]);

	return { suggest, loading, suggestions, panelOpen, setPanelOpen, logId, markAccepted, error };
};

export default useAiSuggest;
